/**
 * Serialize the builder's internal model into WhatsApp Flow JSON.
 *
 * Moved verbatim out of `hooks/useFlowBuilder.js` (`generateFlowJson`). The one
 * change: image processing is gone. It was the only asynchronous step and the
 * only one that needed browser APIs, so it is now a caller-supplied post-pass
 * (see `hooks/useFlowBuilder.js`, which still runs it) and `compile` is a pure,
 * synchronous function of the model.
 *
 * Everything else, auto-naming, If-wrapping of visibility conditions, terminal
 * detection, the routing If-chain, `data_api_version`, `_builderMeta`, encodes
 * hard-won knowledge about what Meta accepts. Do not "tidy" any of it: the
 * backend runs this exact code through GraalJS and the acceptance test is that
 * both hosts emit byte-identical JSON.
 */

import { generateAlphabeticalSuffix } from './idGeneration';
import { getComponentsWithNameField } from './componentUtils';
import { flowComponentRegistry } from './FlowComponentRegistry';
import { conditionToIfExpression, ruleToIfExpression } from './conditionUtils';
import { FLOW_JSON_VERSION, MAX_COMPONENTS_PER_SCREEN, MAX_IF_DEPTH } from './flowValidation';
import { switchRouting } from './routingSwitch';

// Every named input a screen collects, in document order, including inputs nested inside
// If/Switch branches. Reads the SERIALIZED screen so auto-generated names (TextInput_a and
// friends, minted above) are included, they are what the recipient's answers come back
// keyed by.
const collectFieldNames = (children, into) => {
  (children || []).forEach((child) => {
    if (!child || typeof child !== 'object') return;
    if (child.type === 'Footer') return;
    if (typeof child.name === 'string' && child.name) into.push(child.name);
    ['then', 'else', 'children'].forEach((key) => {
      if (Array.isArray(child[key])) collectFieldNames(child[key], into);
    });
    // In the SERIALIZED flow a Switch's `cases` is an object keyed by case value
    // ({"pro": [...]}), not the array of {caseValue, children} the builder model holds.
    // This walks the compiled JSON, so it is the object form that matters here.
    if (child.cases && typeof child.cases === 'object') {
      Object.values(child.cases).forEach((branch) => {
        if (Array.isArray(branch)) collectFieldNames(branch, into);
      });
    }
  });
  return into;
};

/**
 * What COMPONENT produced each answer, so a reader can tell a date from a number.
 *
 * A submission is a flat map of strings: `preferred_date` arrives as "1758758400000" and
 * `quantity` as "12", and nothing in the answer itself says one is a date and the other is not.
 * Guessing from the value is how a thirteen-digit reference number becomes a Tuesday in 2025, so
 * the flow is asked instead.
 *
 * Returns `{ fieldName: componentType }` for every named component that can produce an answer.
 * Same walk as {@link submissionChoices}, for the same reason: a field inside an `If` or a
 * `Switch` is exactly the kind a separate traversal would miss.
 */
export const submissionFieldTypes = (flow) => {
  const out = {};
  const walk = (children) => {
    (children || []).forEach((child) => {
      if (!child || typeof child !== 'object') return;
      if (typeof child.name === 'string' && child.name && typeof child.type === 'string') {
        out[child.name] = child.type;
      }
      ['then', 'else', 'children'].forEach((key) => {
        if (Array.isArray(child[key])) walk(child[key]);
      });
      if (child.cases && typeof child.cases === 'object') {
        Object.values(child.cases).forEach((branch) => {
          if (Array.isArray(branch)) walk(branch);
        });
      }
    });
  };
  ((flow && flow.screens) || []).forEach((screen) => walk(screen.layout && screen.layout.children));
  return out;
};

/**
 * What each choice a customer picked was actually CALLED on screen.
 *
 * A submission carries the machine value, `didi_dance`, `complete_beginner`, because that
 * is what the flow sends. Repeating it back to the business is showing them our plumbing:
 * they wrote "Didi Dance" and expect to read "Didi Dance".
 *
 * Returns `{ fieldName: { value: title } }` for every field backed by a static
 * `data-source`. A dynamic source (`${data.classes}`) has no titles to read at rest and is
 * simply absent, so the caller falls back to the raw value.
 *
 * Lives here, beside `collectFieldNames`, because it needs the same walk: a field inside an
 * `If` or a `Switch` is exactly the kind that a separate traversal would quietly miss.
 */
export const submissionChoices = (flow) => {
  const out = {};
  const walk = (children) => {
    (children || []).forEach((child) => {
      if (!child || typeof child !== 'object') return;
      if (typeof child.name === 'string' && child.name && Array.isArray(child['data-source'])) {
        const titles = {};
        child['data-source'].forEach((item) => {
          if (item && typeof item === 'object' && typeof item.id === 'string') {
            titles[item.id] = item.title;
          }
        });
        if (Object.keys(titles).length > 0) out[child.name] = titles;
      }
      ['then', 'else', 'children'].forEach((key) => {
        if (Array.isArray(child[key])) walk(child[key]);
      });
      if (child.cases && typeof child.cases === 'object') {
        Object.values(child.cases).forEach((branch) => {
          if (Array.isArray(branch)) walk(branch);
        });
      }
    });
  };
  ((flow && flow.screens) || []).forEach((screen) => walk(screen.layout && screen.layout.children));
  return out;
};

// Fill in the payload of a `complete` action that does not have one.
//
// This is what the business actually receives. WhatsApp returns ONLY what the complete
// action names in its payload, an empty payload means the nfm_reply webhook carries a
// flow_token and nothing else, so the flow renders, submits, reports success, and delivers
// no answers at all. Confirmed against a real published flow on 2026-08-28: the completion
// arrived as `{"flow_token": "..."}` with every field the customer had typed missing.
//
// Auto-managed footers are created with an empty payload, so before this every generated
// flow (and every builder flow whose footer was auto-added) silently collected nothing.
// A payload the user or a template set explicitly is left exactly as it is.
//
// Only screens the customer can actually have passed through on the way to this ending are
// included: the ancestors of the terminal screen in the routing graph, in list order. Before
// 2026-09-17 this took every screen earlier in the list, so a policy stop on one branch, or a
// Kurdish ending, referenced fields on screens from other branches and other languages that
// were never shown, which Meta rejects and which would be empty anyway.
const fillCompletionPayload = (compiledScreens, successors) => {
  var indexOf = {};
  compiledScreens.forEach((screen, i) => { indexOf[screen.id] = i; });
  var predecessors = {};
  compiledScreens.forEach((screen) => {
    (successors[screen.id] || []).forEach((next) => {
      (predecessors[next] = predecessors[next] || []).push(screen.id);
    });
  });
  var ancestorsOf = (id) => {
    var seen = new Set([id]);
    var queue = [id];
    while (queue.length) {
      var current = queue.shift();
      (predecessors[current] || []).forEach((prev) => {
        if (!seen.has(prev)) { seen.add(prev); queue.push(prev); }
      });
    }
    return seen;
  };

  compiledScreens.forEach((screen, index) => {
    (screen.layout.children || []).forEach((child) => {
      var action = child && child['on-click-action'];
      if (!action || action.name !== 'complete') return;
      if (action.payload && Object.keys(action.payload).length > 0) return;
      // A terminal screen fed by a data-exchange endpoint is the one case where an empty
      // payload is the right answer and not an oversight: what the business receives is the
      // `extension_message_response` the endpoint returned, which the screen declares. Filling
      // it with `${screen.X.form.y}` references would duplicate that, and name screens the
      // endpoint may have routed straight past (the booking flow skips SERVICE when there is
      // only one), which resolve to nothing.
      if (screen.data && screen.data.extension_message_response) return;

      var reachable = ancestorsOf(screen.id);
      var payload = {};
      compiledScreens.forEach((source, sourceIndex) => {
        if (!reachable.has(source.id)) return;
        collectFieldNames(source.layout.children, []).forEach((field) => {
          // Fields on the submitting screen are `${form.x}`; anything from an earlier
          // screen has to be qualified with that screen's id, or it resolves to nothing.
          payload[field] = sourceIndex === index
            ? '${form.' + field + '}'
            : '${screen.' + source.id + '.form.' + field + '}';
        });
      });
      action.payload = payload;
    });
  });
};

// Where each screen can go next: nothing from a terminal screen, the routing targets when
// the screen routes, else the next in the list. Shared by routing_model and the payload filler.
const computeSuccessors = (screens) => {
  var successors = {};
  screens.forEach((screen, idx) => {
    if (screen.terminal === true) return;
    var targets = new Set();
    var routing = screen.routing;
    var routes = routing?.rules?.length > 0 || Boolean(routing?.defaultNextScreenId);
    if (routes) {
      (routing.rules || []).forEach(rule => { if (rule.nextScreenId) targets.add(rule.nextScreenId); });
      if (routing.defaultNextScreenId) targets.add(routing.defaultNextScreenId);
      else if (idx < screens.length - 1) targets.add(screens[idx + 1].id);
    } else if (idx < screens.length - 1) {
      targets.add(screens[idx + 1].id);
    }
    if (targets.size > 0) successors[screen.id] = Array.from(targets);
  });
  return successors;
};

/**
 * The key a FlowGate flow id travels back under, inside every terminal `complete` payload.
 *
 * WhatsApp returns only what the complete action names in its payload, and the `nfm_reply`
 * webhook that carries a submission says nothing about WHICH flow was submitted, only which
 * WABA and which contact. Stamping the id into the payload is the one channel Meta gives us,
 * so a submission can be attributed to the flow that collected it (and delivered to that
 * flow's destinations).
 *
 * It is our plumbing, not an answer: readers strip it, exactly as they strip `flow_token`.
 */
export const FLOW_ID_FIELD = 'flowgate_flow_id';

// Every `complete` action in a compiled flow, including ones nested in If/Switch branches
// and NavigationList items, the same walk the payload filler needs, for the same reason.
const forEachCompleteAction = (children, fn) => {
  (children || []).forEach((child) => {
    if (!child || typeof child !== 'object') return;
    var action = child['on-click-action'];
    if (action && action.name === 'complete') fn(action);
    (child['list-items'] || []).forEach((item) => {
      var itemAction = item && item['on-click-action'];
      if (itemAction && itemAction.name === 'complete') fn(itemAction);
    });
    ['then', 'else', 'children'].forEach((key) => {
      if (Array.isArray(child[key])) forEachCompleteAction(child[key], fn);
    });
    if (child.cases && typeof child.cases === 'object') {
      Object.values(child.cases).forEach((branch) => {
        if (Array.isArray(branch)) forEachCompleteAction(branch, fn);
      });
    }
  });
};

/**
 * Add `FLOW_ID_FIELD` to every terminal `complete` payload of a compiled flow, in place.
 *
 * Applied to Flow JSON rather than only inside `compile` because the JSON a flow deploys with
 * may have been imported or hand-uploaded, never compiled here at all, and every one of
 * those submissions still has to be attributable.
 *
 * @param {Object} flow  compiled Flow JSON
 * @param {string} flowId  the FlowGate flow id
 * @returns {Object} the same flow object
 */
export const stampFlowId = (flow, flowId) => {
  if (!flow || !flowId) return flow;
  (flow.screens || []).forEach((screen) => {
    forEachCompleteAction(screen.layout && screen.layout.children, (action) => {
      // A non-object payload is not valid Flow JSON, Meta refuses it at deploy, and
      // `payloadProblemsIn` reports it before that, but spreading a string would explode it
      // into character keys, which is a worse thing to hand the validator than the string
      // itself. So it is left exactly as it is, and reported as what it is.
      const payload = action.payload;
      const stampable = payload == null || (typeof payload === 'object' && !Array.isArray(payload));
      if (!stampable) return;
      action.payload = { ...(payload || {}), [FLOW_ID_FIELD]: String(flowId) };
    });
  });
  return flow;
};

/**
 * The field names a submission of this flow comes back with, in payload order.
 *
 * Read off the compiled `complete` payloads, what the flow actually returns, rather than off
 * the components, so a field hidden behind an If or dropped from the payload is reported
 * exactly as the recipient will see it. FlowGate's own `FLOW_ID_FIELD` is excluded: it is
 * plumbing, not an answer.
 *
 * @param {Object} flow  compiled Flow JSON
 * @returns {string[]} field names, de-duplicated, first-seen order
 */
export const submissionFields = (flow) => {
  const seen = [];
  (((flow && flow.screens) || [])).forEach((screen) => {
    forEachCompleteAction(screen.layout && screen.layout.children, (action) => {
      Object.keys(action.payload || {}).forEach((field) => {
        if (field !== FLOW_ID_FIELD && !seen.includes(field)) seen.push(field);
      });
    });
  });
  return seen;
};

/**
 * The "Built with FlowGate" line the Free plan carries.
 *
 * Appended at compile time rather than authored into the model, because it is not the user's
 * content: it must not appear in the builder canvas, survive an edit, or be deletable by
 * removing a component. The backend passes it when the organisation is on Free; nobody else
 * passes it, so a Pro flow is byte-identical to one compiled with no options at all.
 *
 * Placement rules, all load-bearing:
 *   - terminal screens only. This is the screen someone sees once, at the end, having already
 *     decided to answer, the one place a credit costs the business nothing. On every other
 *     screen it would sit between a question and its answer.
 *   - before the Footer, never after: Meta requires the Footer to be the last child of a
 *     layout, and a caption after it is a flow that will not publish. The Footer may be
 *     wrapped in an If (the routing chain), so the insertion point is "before the last child"
 *     rather than "before the component whose type is Footer".
 *   - never twice. `compile(hydrate(x)) === x` is an acceptance criterion of this codebase, and
 *     a branding line that accumulated on each pass would break it on the second round trip.
 *   - never past 50 children, WhatsApp's hard cap on a screen. A flow that fills its last
 *     screen loses the credit rather than losing the ability to publish.
 */
export const applyBranding = (flowJson, branding) => {
  const text = branding && typeof branding.text === 'string' ? branding.text.trim() : '';
  if (!text) return flowJson;

  (flowJson.screens || []).forEach((screen) => {
    if (!screen || screen.terminal !== true) return;
    const children = screen.layout && screen.layout.children;
    if (!Array.isArray(children)) return;
    if (children.some((c) => c && c.type === 'TextCaption' && c.text === text)) return;
    if (children.length >= MAX_COMPONENTS_PER_SCREEN) return;

    const caption = { type: 'TextCaption', text: text };
    const last = children[children.length - 1];
    const footerIsLast = last && (last.type === 'Footer' || last.type === 'If');
    if (footerIsLast) {
      children.splice(children.length - 1, 0, caption);
    } else {
      children.push(caption);
    }
  });

  return flowJson;
};

// Build a Footer JSON object pointing at a specific target screen (or complete action).
function buildFooterForTarget(baseFooterJson, targetScreenId) {
  if (!targetScreenId) {
    return {
      type: 'Footer',
      label: baseFooterJson.label,
      'on-click-action': { name: 'complete', payload: {} },
    };
  }
  return {
    type: 'Footer',
    label: baseFooterJson.label,
    'on-click-action': {
      name: 'navigate',
      next: { type: 'screen', name: targetScreenId },
    },
  };
}

/** The rules that compile to a branch: a condition and a target. */
export function compilableRoutingRules(rules) {
  return (rules || []).filter(rule => rule && rule.nextScreenId && ruleToIfExpression(rule));
}

// Build a nested If chain from routing rules (last-to-first for first-rule-wins semantics).
// One If per rule, so MAX_IF_DEPTH rules at most; past that the screen routes with a Switch
// (see routingSwitch.js) or the validator refuses it.
function buildIfTree(rules, baseFooterJson, defaultFooter) {
  var elseContent = [defaultFooter];
  for (var i = rules.length - 1; i >= 0; i--) {
    var rule = rules[i];
    var conditionExpr = ruleToIfExpression(rule);
    if (!conditionExpr || !rule.nextScreenId) continue;
    var thenFooter = buildFooterForTarget(baseFooterJson, rule.nextScreenId);
    elseContent = [{
      type: 'If',
      condition: conditionExpr,
      then: [thenFooter],
      else: elseContent,
    }];
  }
  // If no valid rules existed, fall back to the default footer directly
  return elseContent[0];
}

// A Switch on the routed field: one case per option, each holding the Footer for that option's
// target, the default target for options no rule names. Meta accepted this shape on 2026-09-18.
function buildRoutingSwitch(plan, baseFooterJson, defaultFooter) {
  var cases = {};
  plan.cases.forEach(entry => {
    cases[entry.value] = [entry.nextScreenId ? buildFooterForTarget(baseFooterJson, entry.nextScreenId) : defaultFooter];
  });
  return { type: 'Switch', value: '${form.' + plan.field + '}', cases: cases };
}

/**
 * @param {Array} screens - the builder's internal screens model
 * @param {Object} [options] - `{ flowId }` stamps the FlowGate flow id into every terminal
 *   `complete` payload, so the submission webhook says which flow was submitted.
 *   `{ branding: { text } }` appends that line as a TextCaption on terminal screens only,
 *   immediately before the Footer, the Free plan's "Built with FlowGate" credit.
 * @returns {Object} WhatsApp Flow JSON (without image post-processing)
 */
export const compile = (screens, options) => {
  const componentsWithName = new Set(getComponentsWithNameField());
  const componentsWithId = new Set([]);

  // Reset counts for id generation
  const typeCounters = {};

  // Helper to check if a screen is terminal (has complete action)
  var isScreenTerminal = (screen, isLastScreen) => {
    // If explicitly marked terminal, use that
    if (screen.terminal) return true;

    // A screen that routes somewhere is not an ending, wherever it sits in the list. The last
    // screen of a localised flow's last language chain used to be made terminal here because its
    // auto footer said "complete", and its routing to the details screen was then ignored.
    var routing = screen.routing;
    if (routing?.defaultNextScreenId || (routing?.rules || []).some(rule => rule && rule.nextScreenId)) {
      return false;
    }

    // Check if Footer has complete action
    var footer = screen.components.find(c => c.type === 'Footer');
    if (footer && footer.properties?.['on-click-action']?.name === 'complete') {
      return true;
    }

    // For last screen, mark as terminal if no navigate actions
    if (isLastScreen) {
      var hasNavigateAction = screen.components.some(c => {
        if (c.type === 'Footer') {
          return c.properties?.['on-click-action']?.name === 'navigate';
        }
        if (c.type === 'NavigationList') {
          return (c.properties?.['list-items'] || []).some(
            item => item['on-click-action']?.name === 'navigate' && item['on-click-action']?.next?.name
          );
        }
        return false;
      });
      return !hasNavigateAction;
    }

    return false;
  };

  const flowJson = {
    version: FLOW_JSON_VERSION,
    screens: screens.map((screen, index) => {
      var isLast = index === screens.length - 1;
      var terminal = isScreenTerminal(screen, isLast);

      // A dynamic screen declares what its `${data.x}` references resolve to. Meta needs
      // the declaration (with `__example__`) at publish time; the endpoint fills it at
      // runtime. Emitted before `layout` to match Meta's own examples.
      var screenData = Object.assign(
        { id: screen.id, title: screen.title },
        screen.data && Object.keys(screen.data).length > 0 ? { data: screen.data } : {},
        {
        layout: {
          type: "SingleColumnLayout",
          children: screen.components.map(component => {
            // Strip _condition before passing to plugin transform
            var { _condition, ...propsWithoutCondition } = component.properties;

            // Use component registry to transform properties to WhatsApp format
            var registeredComponent = flowComponentRegistry.get(component.type);
            var componentData = registeredComponent
              ? registeredComponent.transformToWhatsApp(propsWithoutCondition)
              : { type: component.type, ...propsWithoutCondition };

            // Add 'name' field for components that require it (if not already set by transform)
            if (componentsWithName.has(component.type) && !componentData.name) {
              var cleanType = component.type.replace(/[^a-zA-Z]/g, '');
              var currentCount = typeCounters[cleanType] || 0;
              var suffix = generateAlphabeticalSuffix(currentCount);
              typeCounters[cleanType] = currentCount + 1;

              componentData.name = `${cleanType}_${suffix}`;
            }

            // Add 'id' field for components that specifically use 'id' instead of 'name'
            if (componentsWithId.has(component.type) && !componentData.id) {
              var cleanType2 = component.type.replace(/[^a-zA-Z]/g, '');
              var currentCount2 = typeCounters[cleanType2] || 0;
              var suffix2 = generateAlphabeticalSuffix(currentCount2);
              typeCounters[cleanType2] = currentCount2 + 1;

              componentData.id = `${cleanType2}_${suffix2}`;
            }

            // Visibility conditions: Meta rejects rich expressions in the
            // `visible` field (boolean refs only), so wrap conditional
            // components in an If block. Round-trip happens via
            // _builderMeta.componentConditions (written below) and the
            // import-side findAutoWrappedCondition detector.
            //
            // Footer is excluded because (a) the routing logic later in this
            // function locates the Footer by `c.type === 'Footer'` and an
            // If wrapper would break that lookup, and (b) PropertiesPanel's
            // NO_CONDITION_TYPES already blocks the UI from setting
            // _condition on a Footer, but flows imported from external
            // editors could carry one.
            delete componentData.visible;
            if (_condition && component.type !== 'Footer') {
              var ifExpr = conditionToIfExpression(_condition);
              if (ifExpr) {
                return {
                  type: 'If',
                  condition: ifExpr,
                  then: [componentData],
                };
              }
            }

            return componentData;
          })
        }
      });

      if (terminal) {
        screenData.terminal = true;
      }

      // Routing: rules wrap the Footer in an If chain; a default target alone (a screen whose
      // "next" is not the next in the list, which a branching flow is full of) just points the
      // Footer there. A terminal screen ends the flow whatever its routing says.
      var routing = screen.routing;
      var hasRules = routing?.rules?.length > 0;
      var hasDefault = Boolean(routing?.defaultNextScreenId);
      if (!terminal && (hasRules || hasDefault)) {
        var children = screenData.layout.children;
        var footerIndex = children.findIndex(c => c.type === 'Footer');
        var footerJson = footerIndex >= 0 ? children[footerIndex] : null;

        if (footerJson) {
          var childrenWithoutFooter = children.filter(c => c.type !== 'Footer');
          var defaultNextId = routing.defaultNextScreenId ||
            (index < screens.length - 1 ? screens[index + 1].id : null);
          var defaultFooterJson = buildFooterForTarget(footerJson, defaultNextId);
          // Up to three rules: the If chain the builder shows. More: a Switch, when the routed
          // field allows one (routingSwitch.js); otherwise the chain, which validation refuses.
          var switchPlan = compilableRoutingRules(routing.rules).length > MAX_IF_DEPTH ? switchRouting(screen) : null;
          screenData.layout.children = hasRules
            ? [...childrenWithoutFooter, switchPlan
              ? buildRoutingSwitch(switchPlan, footerJson, defaultFooterJson)
              : buildIfTree(routing.rules, footerJson, defaultFooterJson)]
            : [...childrenWithoutFooter, defaultFooterJson];
        }
      }

      return screenData;
    })
  };

  var successors = computeSuccessors(screens);
  fillCompletionPayload(flowJson.screens, successors);
  if (options && options.branding) {
    applyBranding(flowJson, options.branding);
  }
  if (options && options.flowId) {
    stampFlowId(flowJson, options.flowId);
  }

  // Dynamic (data-exchange) flows must declare data_api_version so Meta knows
  // to call the registered endpoint. The robust signal is the presence of any
  // data_exchange on-click-action, on a Footer, any component, or a
  // NavigationList item.
  var hasDataExchange = screens.some(screen =>
    screen.components.some(c => {
      if (c.properties?.['on-click-action']?.name === 'data_exchange') {
        return true;
      }
      if (c.type === 'NavigationList') {
        return (c.properties?.['list-items'] || []).some(
          item => item['on-click-action']?.name === 'data_exchange'
        );
      }
      return false;
    })
  );
  if (hasDataExchange) {
    flowJson.data_api_version = "3.0";
  }

  // routing_model lists the screens each screen can actually go to.
  var routingModel = successors;
  if (Object.keys(routingModel).length > 0) {
    flowJson.routing_model = routingModel;
  }

  // Attach builder metadata for round-trip preservation (stripped before sending to Meta)
  var screenRoutingMeta = {};
  screens.forEach(s => {
    // Rules AND a bare default: a screen whose "next" is not the next in the list (every branch
    // ending in a branching flow) lost its target on reload when only rules were kept, and the
    // recompiled routing_model then disagreed with its Footer (Meta: "Missing direct route", 2026-09-18).
    if (s.routing?.rules?.length > 0 || s.routing?.defaultNextScreenId) screenRoutingMeta[s.id] = s.routing;
  });
  // Round-trip metadata for visibility conditions. Keyed by the position
  // in screen.components, positional rather than by name so that:
  //   - name-less display components (TextBody, TextHeading, Image, …)
  //     round-trip too,
  //   - components with auto-generated names round-trip,
  //   - duplicate names within a screen don't collide.
  // Footer is skipped to match the wrap-side guard.
  var componentConditionsMeta = {};
  screens.forEach(s => {
    s.components.forEach((c, index) => {
      if (c.properties._condition && c.type !== 'Footer') {
        componentConditionsMeta[`${s.id}:${index}`] = c.properties._condition;
      }
    });
  });
  if (Object.keys(screenRoutingMeta).length > 0 || Object.keys(componentConditionsMeta).length > 0) {
    flowJson._builderMeta = {
      screenRouting: screenRoutingMeta,
      componentConditions: componentConditionsMeta,
    };
  }

  return flowJson;
};
